import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const AuthContext = createContext(null);

/** Background revalidation cadence for `/auth/me` while the tab is visible.
 *  60 s is a sensible upper bound for "an admin flipped a perm, how long
 *  until I see it" without putting meaningful load on the API. */
const REVALIDATE_INTERVAL_MS = 60_000;

/** Don't issue an /auth/me call more than once per this window — debounces
 *  the focus + visibility + route-change handlers when they fire in quick
 *  succession (e.g. focus + visibility almost always co-occur). */
const REVALIDATE_DEBOUNCE_MS = 1_500;

/** Push the active tenant's brand palette into CSS variables so
 *  components reading `bg-[var(--brand-primary)]` / similar automatically
 *  retheme on tenant switch. */
function applyBranding(tenant) {
  const root = document.documentElement;
  const b = tenant?.branding || {};
  root.style.setProperty("--brand-primary", b.primary_color || "#0f172a");
  root.style.setProperty("--brand-accent", b.accent_color || "#d4af37");
  if (b.background_tint) {
    root.style.setProperty("--brand-tint", b.background_tint);
  } else {
    root.style.removeProperty("--brand-tint");
  }
  root.dataset.tenant = tenant?.slug || "";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [activeTenant, setActiveTenant] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  // Ref-based debounce so we don't dogpile /auth/me when focus, visibility
  // and a route change all fire in the same tick.
  const lastRefreshAtRef = useRef(0);
  const inflightRef = useRef(null);
  const hasUserRef = useRef(false);

  const refresh = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force && now - lastRefreshAtRef.current < REVALIDATE_DEBOUNCE_MS) {
      // Coalesce: return the in-flight promise if there is one, else skip.
      return inflightRef.current;
    }
    lastRefreshAtRef.current = now;
    const p = (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        hasUserRef.current = true;
        setActiveTenant(data.active_tenant || null);
        setMemberships(data.memberships || []);
        if (data.active_tenant) applyBranding(data.active_tenant);
        return data;
      } catch (e) {
        setUser(null);
        hasUserRef.current = false;
        setActiveTenant(null);
        setMemberships([]);
        applyBranding(null);
        return null;
      } finally {
        inflightRef.current = null;
      }
    })();
    inflightRef.current = p;
    return p;
  }, []);

  // Initial mount: always force a fresh /auth/me.
  useEffect(() => {
    let alive = true;
    (async () => {
      await refresh({ force: true });
      if (alive) setIsLoading(false);
    })();
    return () => { alive = false; };
  }, [refresh]);

  // Revalidate on focus + visibilitychange — picks up role/permission
  // edits that another admin made in a different tab.
  useEffect(() => {
    const onFocus = () => {
      if (hasUserRef.current) refresh();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && hasUserRef.current) {
        refresh();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  // Belt-and-braces: while the tab is visible, poll /auth/me every 60s.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible" && hasUserRef.current) {
        refresh();
      }
    }, REVALIDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Route change → revalidate. Skips the very first render (initial mount
  // already forces a refresh) and skips when there's no authenticated user.
  const firstNavRef = useRef(true);
  useEffect(() => {
    if (firstNavRef.current) {
      firstNavRef.current = false;
      return;
    }
    if (hasUserRef.current) refresh();
  }, [location.pathname, refresh]);

  const login = useCallback(async ({ email, password, remember }) => {
    const { data } = await api.post("/auth/login", { email, password, remember: !!remember });
    setActiveTenant(data.active_tenant || null);
    setMemberships(data.memberships || []);
    if (data.active_tenant) applyBranding(data.active_tenant);
    await refresh({ force: true });
    return data.user;
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    finally {
      setUser(null);
      hasUserRef.current = false;
      setActiveTenant(null);
      setMemberships([]);
      applyBranding(null);
    }
  }, []);

  const switchTenant = useCallback(async (tenant_id) => {
    const { data } = await api.post("/auth/switch-tenant", { tenant_id });
    setUser(data);
    hasUserRef.current = true;
    setActiveTenant(data.active_tenant || null);
    if (data.active_tenant) applyBranding(data.active_tenant);
    return data;
  }, []);

  const hasPerm = useCallback(
    (key) => Boolean(user?.permissions?.[key]),
    [user]
  );

  const value = useMemo(
    () => ({ user, activeTenant, memberships, isLoading, login, logout, refresh, switchTenant, hasPerm }),
    [user, activeTenant, memberships, isLoading, login, logout, refresh, switchTenant, hasPerm]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
